import { memo, useCallback, useMemo } from "react";
import type { ReactElement } from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { styled } from "@mui/material";
import TooltipHelper from "components/TooltipHelper";
import ArrowDownIcon from "components/Icons/ArrowDownIcon";
import type { DataTableColumn, SortState } from "../VirtualizedDataTable";
import IconButton from "components/IconButton";
import VisuallyHidden from "components/VisuallyHidden";

const StyledTableCellHeader = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-root": {
    color: theme.palette.grey[120],
  },

  ...theme.typography.TextS,
  fontFamily: theme.typography.fontFamily,

  "&.MuiTableCell-sizeSmall": {
    padding: "6px 2px",
  },

  "&.MuiTableCell-sizeMedium": {
    padding: "12px 16px",
  },

  "&[data-theme='dark']": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  // Collapses the cell but keeps it in the table, so the header stays announced by screen readers.
  "&.MuiTableCell-root[data-visually-hidden]": {
    // Anchors the absolutely positioned label so it can't escape the scroll container and grow the page.
    position: "relative",
    height: 0,
    padding: 0,
    border: 0,
    lineHeight: 0,
  },
}));

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  color: "inherit",

  "& .MuiTableSortLabel-icon": {
    color: "inherit",
    fontSize: "inherit",
    marginLeft: "2px",
    marginRight: "2px",
    transition: theme.transitions.create(["opacity", "transform"], {
      duration: theme.transitions.duration.shortest,
    }),

    "&:hover": {
      opacity: 1,
      backgroundColor: theme.palette.grey[20],
      color: theme.palette.primary.main,
    },
  },

  "&.Mui-active": {
    color: "inherit",
    "& .MuiTableSortLabel-icon": {
      color: "inherit",
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
  },

  "&:hover": {
    color: "inherit",
    "&.Mui-active .MuiTableSortLabel-icon": {
      opacity: 1,
    },
  },

  "&:focus": {
    color: "inherit",
  },

  "&[data-theme='dark']": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,

    "&.MuiTableSortLabel-root:hover": {
      color: theme.palette.common.white,
    },

    "& .MuiTableSortLabel-icon": {
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: "transparent",
      },
    },

    "&.Mui-active .MuiTableSortLabel-icon": {
      color: theme.palette.common.white,
    },
  },
}));

function IconComponent(props: { className?: string; size?: "small" | "medium" }) {
  const { size, ...rest } = props;
  const buttonSize = useMemo(() => (size === "small" ? "extraSmall" : "small"), [size]);
  return (
    <IconButton component="span" {...rest} size={buttonSize}>
      <ArrowDownIcon fontSize={buttonSize} />
    </IconButton>
  );
}

type HeadRowSlotProps = React.ComponentProps<typeof TableRow>;
type HeadCellSlotProps = React.ComponentProps<typeof TableCell>;

interface DataTableHeadRowProps<T> {
  /** 0-based position of this row among all rows in the table */
  tableRowIndex: number;
  columns: readonly DataTableColumn<T>[];
  sort: SortState<T> | null;
  size: "small" | "medium";
  onSort: (columnId: keyof T) => void;
  visuallyHidden?: boolean;
  slotProps?: {
    headRow?: HeadRowSlotProps;
    headCell?: HeadCellSlotProps;
  };
  theme?: "light" | "dark";
}

function DataTableHeadRowImpl<T>(props: DataTableHeadRowProps<T>) {
  const { tableRowIndex, columns, sort, size, onSort, visuallyHidden, slotProps } = props;

  const curriedOnSort = useCallback((columnId: keyof T) => () => onSort(columnId), [onSort]);

  return (
    <TableRow aria-rowindex={tableRowIndex + 1} {...slotProps?.headRow}>
      {columns.map((column, index) => {
        const isSorted = sort?.columnId === column.id;
        const sortDirection = isSorted ? sort.direction : (column.initialSortDirection ?? "asc");
        const ariaSort = isSorted ? (sort.direction === "asc" ? "ascending" : "descending") : undefined;
        return (
          <StyledTableCellHeader
            key={String(column.id ?? index)}
            align={column.align}
            sortDirection={sortDirection}
            style={{ minWidth: column.minWidth, width: column.width, maxWidth: column.maxWidth }}
            aria-sort={ariaSort}
            aria-label={typeof column.header === "string" ? column.header : undefined}
            data-theme={props.theme}
            data-visually-hidden={visuallyHidden || undefined}
            {...slotProps?.headCell}
          >
            {visuallyHidden ? (
              <VisuallyHidden>{column.header}</VisuallyHidden>
            ) : column.id && column.sortComparator ? (
              <TooltipHelper title={column.sortTitle?.(sortDirection)} enterDelay={500}>
                <StyledTableSortLabel
                  IconComponent={(props) => <IconComponent {...props} size={size} />}
                  active={isSorted}
                  direction={sortDirection}
                  onClick={curriedOnSort(column.id)}
                  data-theme={props.theme}
                >
                  {column.header}
                </StyledTableSortLabel>
              </TooltipHelper>
            ) : (
              column.header
            )}
          </StyledTableCellHeader>
        );
      })}
    </TableRow>
  );
}

const DataTableHeadRow = memo(DataTableHeadRowImpl) as <T>(props: DataTableHeadRowProps<T>) => ReactElement;

export default DataTableHeadRow;
